import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import '../../modalright.css';
import '../../popup.css';
import '../../pricing-strategies.css';
import '../../wallet.css';
import '../../wallet-fixes.css';
import '../../custody.css';
import { getDriverApiBaseUrl } from '../../utils/utils';
import {
    formatMoney, formatInt, formatDateTime,
    Person, Pill, StatCard, EmptyState, WalletSpinner,
    useWalletToasts, WalletToastStack, SignaturePad,
} from './walletShared';
import {
    fetchDriverRunsheet, fetchSupervisorHeld, fetchAccountHeld, fetchAwbTrail, fetchLedger,
    handoffDriverToSupervisor, handoffSupervisorToAccount, handoffAccountToCompany,
    custodyCurrency, stageMeta, stageTimestamp, CUSTODY_STAGES,
    AwbChecklist, useSelectedTotals, CustodyTimeline,
} from './custodyShared';

// Local date as YYYY-MM-DD (avoids the UTC shift of toISOString()).
const todayLocal = () => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};

// Per-AWB "chain of custody" — collectors/accountants pick whole AWBs (no partial
// amounts; the amount is the sum of the picked AWBs) at each hand-off, and anyone
// can look up an AWB's full Driver → Supervisor → Account → Company trail.
export default function AwbCustody() {
    const API_BASE = `${getDriverApiBaseUrl()}/shypvdriverapp/`;
    const email = sessionStorage.getItem('useremail') || '';
    const authHeaders = () => {
        const t = sessionStorage.getItem('usertoken');
        return t ? { Authorization: `Bearer ${t}` } : {};
    };
    const { toasts, toast, dismiss } = useWalletToasts();
    const location = useLocation();

    // ?tab= deep-link (e.g. supervisor page links to ?tab=from-driver)
    const initialTab = useMemo(() => {
        const t = new URLSearchParams(location.search).get('tab');
        return ['from-driver', 'from-supervisor', 'to-company', 'trail', 'ledger'].includes(t) ? t : 'from-driver';
    }, [location.search]);
    const [tab, setTab] = useState(initialTab);
    useEffect(() => { setTab(initialTab); }, [initialTab]);

    // Party pickers (reuse the existing wallet sources)
    const [drivers, setDrivers] = useState([]);        // admin/wallet/balances
    const [supervisors, setSupervisors] = useState([]); // COLLECTOR roles
    const [accountants, setAccountants] = useState([]); // ACCOUNTANT roles
    const [partiesLoading, setPartiesLoading] = useState(true);

    const [selectedDriverId, setSelectedDriverId] = useState('');
    const [selectedSupervisorId, setSelectedSupervisorId] = useState('');
    const [selectedAccountantId, setSelectedAccountantId] = useState('');
    // COD collection is amount-based, not run-sheet-date-based: always show every collectible AWB
    // the driver is still holding (all dates). Kept as a constant so the fetch/deps stay unchanged.
    const runsheetDate = '';

    // Held-AWB lists per stage
    const [heldItems, setHeldItems] = useState([]);
    const [heldLoading, setHeldLoading] = useState(false);
    const [selected, setSelected] = useState(new Set());

    // Confirm (signature) modal
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [signature, setSignature] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    // Trail lookup
    const [trailInput, setTrailInput] = useState('');
    const [trail, setTrail] = useState(null);
    const [trailLoading, setTrailLoading] = useState(false);
    const [trailSearched, setTrailSearched] = useState(false);

    // COD Trace (ledger) — every AWB, amount, current stage and who holds the cash
    const [ledger, setLedger] = useState([]);
    const [ledgerLoading, setLedgerLoading] = useState(false);
    const [ledgerSearch, setLedgerSearch] = useState('');
    const [ledgerStage, setLedgerStage] = useState('ALL');

    const totals = useSelectedTotals(heldItems, selected);
    const heldCurrency = custodyCurrency(heldItems);

    // Everything the driver is still holding (the "remaining to collect"), regardless of selection.
    const allHeldSet = useMemo(() => new Set(heldItems.map(a => a.awb)), [heldItems]);
    const remaining = useSelectedTotals(heldItems, allHeldSet);
    const selectedDriver = useMemo(
        () => drivers.find(d => String(d.driverId) === String(selectedDriverId)) || null,
        [drivers, selectedDriverId]
    );

    // COD Trace filtering + totals
    const ledgerFiltered = useMemo(() => {
        const q = ledgerSearch.trim().toLowerCase();
        return ledger.filter(r =>
            (ledgerStage === 'ALL' || r.stage === ledgerStage) &&
            (!q || [r.awb, r.driverName, r.holderName].some(v => (v || '').toLowerCase().includes(q)))
        );
    }, [ledger, ledgerSearch, ledgerStage]);
    const ledgerTotals = useMemo(() => ledgerFiltered.reduce((acc, r) => {
        acc.cod += Number(r.codAmount || 0);
        acc.dt += Number(r.dutiesTaxAmount || 0);
        acc.total += Number(r.amount || 0);
        return acc;
    }, { cod: 0, dt: 0, total: 0 }), [ledgerFiltered]);
    const ledgerCurrency = (ledger.find(r => r.currency) || {}).currency || 'AED';

    // ── Load party pickers once ────────────────────────────────────────────────
    useEffect(() => {
        let alive = true;
        (async () => {
            setPartiesLoading(true);
            try {
                const [b, r] = await Promise.all([
                    axios.get(`${API_BASE}admin/wallet/balances`, { headers: authHeaders() }),
                    axios.get(`${API_BASE}admin/wallet/roles`, { headers: authHeaders() }),
                ]);
                if (!alive) return;
                setDrivers((b.data && b.data.data) || []);
                const roleRows = (r.data && r.data.data) || [];
                setSupervisors(roleRows.filter(x => x.role === 'COLLECTOR'));
                const accts = roleRows.filter(x => x.role === 'ACCOUNTANT');
                setAccountants(accts);
                // Default the Account Submit picker to the logged-in accountant, matched by EMAIL.
                // This yields the correct wallet user_id (the custody holder id) — which is NOT the
                // same as sessionStorage 'userid' (that stores personnel_id), the reason the account
                // list came up empty before.
                const mine = accts.find(a => (a.email || '').toLowerCase() === (email || '').toLowerCase());
                if (mine) setSelectedAccountantId(String(mine.userId));
            } catch (err) {
                console.error(err);
                if (alive) toast.error('Failed to load drivers / supervisors.');
            } finally {
                if (alive) setPartiesLoading(false);
            }
        })();
        return () => { alive = false; };
        /* eslint-disable-next-line */
    }, []);

    // ── Fetch the held list for the active tab ─────────────────────────────────
    const clearSelection = () => setSelected(new Set());

    const loadHeld = async () => {
        setSelected(new Set());
        if (tab === 'from-driver') {
            if (!selectedDriverId) { setHeldItems([]); return; }
            setHeldLoading(true);
            try { setHeldItems(await fetchDriverRunsheet(selectedDriverId, runsheetDate)); }
            catch (err) { console.error(err); toast.error('Failed to load the driver’s run sheet.'); setHeldItems([]); }
            finally { setHeldLoading(false); }
        } else if (tab === 'from-supervisor') {
            if (!selectedSupervisorId) { setHeldItems([]); return; }
            setHeldLoading(true);
            try { setHeldItems(await fetchSupervisorHeld(selectedSupervisorId)); }
            catch (err) { console.error(err); toast.error('Failed to load the supervisor’s held AWBs.'); setHeldItems([]); }
            finally { setHeldLoading(false); }
        } else if (tab === 'to-company') {
            if (!selectedAccountantId) { setHeldItems([]); return; }
            setHeldLoading(true);
            try { setHeldItems(await fetchAccountHeld(selectedAccountantId)); }
            catch (err) { console.error(err); toast.error('Failed to load the accountant’s held AWBs.'); setHeldItems([]); }
            finally { setHeldLoading(false); }
        }
    };

    // Auto-refetch when the tab, the chosen party, or the run-sheet date changes.
    useEffect(() => {
        if (tab === 'trail') { setHeldItems([]); return; }
        loadHeld();
        /* eslint-disable-next-line */
    }, [tab, selectedDriverId, selectedSupervisorId, selectedAccountantId, runsheetDate]);

    // ── Selection helpers ──────────────────────────────────────────────────────
    const toggle = (awb) => setSelected(prev => {
        const next = new Set(prev);
        if (next.has(awb)) next.delete(awb); else next.add(awb);
        return next;
    });
    const toggleAll = () => setSelected(prev => {
        if (heldItems.length && heldItems.every(a => prev.has(a.awb))) return new Set();
        return new Set(heldItems.map(a => a.awb));
    });

    // ── Confirm hand-off (signature) ───────────────────────────────────────────
    const signerLabel = tab === 'from-driver' ? 'driver' : tab === 'from-supervisor' ? 'supervisor' : 'you';
    const openConfirm = () => {
        if (!selected.size) return toast.error('Select at least one AWB to hand over.');
        setSignature(''); setNotes(''); setConfirmOpen(true);
    };

    const submitHandoff = async () => {
        if (!signature) return toast.error('Please sign to confirm the hand-off.');
        const awbs = Array.from(selected);
        setSaving(true);
        let out;
        if (tab === 'from-driver') {
            out = await handoffDriverToSupervisor({ awbs, driverId: selectedDriverId, signature, notes, email });
        } else if (tab === 'from-supervisor') {
            out = await handoffSupervisorToAccount({ awbs, supervisorUserId: selectedSupervisorId, signature, notes, email });
        } else {
            out = await handoffAccountToCompany({ awbs, signature, notes, email });
        }
        setSaving(false);
        if (out.ok) {
            const r = out.result || {};
            const cur = r.currency || heldCurrency;
            const cod = r.totalCod != null ? r.totalCod : totals.cod;
            const duty = r.totalDutiesTax != null ? r.totalDutiesTax : totals.dutiesTax;
            const total = r.totalAmount != null ? r.totalAmount : totals.total;
            toast.success(
                `Handed over ${formatInt(r.awbCount != null ? r.awbCount : awbs.length)} AWB(s) — ` +
                `COD ${formatMoney(cod, cur)} + Duty/Tax ${formatMoney(duty)} = ${formatMoney(total, cur)}.`
            );
            setConfirmOpen(false);
            loadHeld();
        } else {
            toast.error(out.error || 'Hand-off failed.');
        }
    };

    // ── Trail lookup ───────────────────────────────────────────────────────────
    const runTrail = async () => {
        const awb = trailInput.trim();
        if (!awb) return;
        setTrailLoading(true);
        setTrailSearched(true);
        try {
            setTrail(await fetchAwbTrail(awb));
        } catch (err) {
            console.error(err);
            setTrail(null);
            toast.error('Could not load the trail for that AWB.');
        } finally {
            setTrailLoading(false);
        }
    };

    // Open an AWB's full trail from the COD Trace list.
    const openTrail = async (awb) => {
        setTab('trail');
        setTrailInput(awb);
        setTrailSearched(true);
        setTrailLoading(true);
        try {
            setTrail(await fetchAwbTrail(awb));
        } catch (err) {
            console.error(err);
            setTrail(null);
            toast.error('Could not load the trail for that AWB.');
        } finally {
            setTrailLoading(false);
        }
    };

    // ── Load the COD Trace ledger when its tab opens ───────────────────────────
    const loadLedger = async () => {
        setLedgerLoading(true);
        try { setLedger(await fetchLedger() || []); }
        catch (err) { console.error(err); toast.error('Failed to load the COD trace.'); setLedger([]); }
        finally { setLedgerLoading(false); }
    };
    useEffect(() => {
        if (tab === 'ledger') loadLedger();
        /* eslint-disable-next-line */
    }, [tab]);

    // ── Tab config ─────────────────────────────────────────────────────────────
    const TABS = [
        { key: 'from-driver', icon: 'fa-truck', label: 'Driver Collection' },
        { key: 'from-supervisor', icon: 'fa-user-secret', label: 'Supervisor Collection' },
        { key: 'to-company', icon: 'fa-university', label: 'Account Submit' },
        { key: 'ledger', icon: 'fa-list-ul', label: 'COD Trace' },
        { key: 'trail', icon: 'fa-list-alt', label: 'AWB Trail' },
    ];

    const isHandoffTab = tab === 'from-driver' || tab === 'from-supervisor' || tab === 'to-company';
    const partyChosen =
        tab === 'from-driver' ? !!selectedDriverId :
            tab === 'from-supervisor' ? !!selectedSupervisorId :
                tab === 'to-company' ? !!selectedAccountantId : false;

    const confirmCta =
        tab === 'from-driver' ? 'Collect selected AWBs' :
            tab === 'from-supervisor' ? 'Collect selected AWBs' : 'Deposit selected AWBs';

    return (
        <div className="wallet-page wui">
            <WalletToastStack toasts={toasts} onDismiss={dismiss} />

            <div className="w-topbar">
                <div className="w-topbar-left">
                    <div className="w-icon-badge"><i className="fa fa-exchange" aria-hidden="true" /></div>
                    <div>
                        <h2 className="w-title">Wallet — Collections</h2>
                        <p className="w-subtitle">
                            Collect COD cash by whole AWB: Driver Collection → Supervisor Collection → Account Submit.
                            Signed in as <strong>{email || 'unknown'}</strong>.
                        </p>
                    </div>
                </div>
                <div className="w-topbar-actions">
                    {isHandoffTab && (
                        <button className="w-btn w-btn-ghost" onClick={loadHeld} disabled={heldLoading || !partyChosen}>
                            <i className={`fa fa-refresh ${heldLoading ? 'fa-spin' : ''}`} aria-hidden="true" /> Refresh
                        </button>
                    )}
                    <a className="w-btn w-btn-ghost" href="/orders/wallet-setup" title="Assign collectors & accountants">
                        <i className="fa fa-sliders" aria-hidden="true" /> Wallet Setup
                    </a>
                </div>
            </div>

            {/* Tabs */}
            <div className="w-chip-group" style={{ marginBottom: 16 }}>
                {TABS.map(t => (
                    <button key={t.key} className={`w-chip ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
                        <i className={`fa ${t.icon}`} aria-hidden="true" /> {t.label}
                    </button>
                ))}
            </div>

            {/* ── Hand-off tabs (driver / supervisor / company) ── */}
            {isHandoffTab && (
                <>
                    <div className="w-card">
                        <div className="w-card-header">
                            <span className="w-card-title">
                                <i className="fa fa-filter" aria-hidden="true" />
                                {tab === 'from-driver' ? ' Choose a driver' : tab === 'from-supervisor' ? ' Choose a supervisor' : ' Your held AWBs'}
                            </span>
                            <span className="w-card-meta">
                                {tab === 'from-driver' && `${formatInt(drivers.length)} driver(s)`}
                                {tab === 'from-supervisor' && `${formatInt(supervisors.length)} supervisor(s)`}
                                {tab === 'to-company' && `${formatInt(accountants.length)} accountant(s)`}
                            </span>
                        </div>
                        <div className="w-card-body">
                            {tab === 'from-driver' && (
                                <>
                                    <div className="form-group" style={{ marginBottom: 0, maxWidth: 520 }}>
                                        <label>Driver</label>
                                        <select className="form-control" value={selectedDriverId}
                                            onChange={e => setSelectedDriverId(e.target.value)} disabled={partiesLoading}>
                                            <option value="">{partiesLoading ? 'Loading drivers…' : 'Select a driver…'}</option>
                                            {drivers.map(d => (
                                                <option key={d.driverId} value={d.driverId}>
                                                    {(d.driverName || `Driver #${d.driverId}`)}{d.phoneNumber ? ` · ${d.phoneNumber}` : ''}
                                                    {Number(d.outstanding) > 0 ? ` — ${formatMoney(d.outstanding, d.currency)} outstanding` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <small style={{ color: '#6b7280' }}>Showing everything this driver is holding — all dates.</small>
                                    </div>

                                    {/* Remaining amount for the chosen driver (COD + duties/tax across all held AWBs) */}
                                    {selectedDriverId && (
                                        <div style={{
                                            marginTop: 14, display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center',
                                            padding: '12px 16px', borderRadius: 12, background: '#f0f9ff', border: '1px solid #bae6fd',
                                        }}>
                                            <span style={{ fontWeight: 700, color: '#075985' }}>
                                                <i className="fa fa-wallet" aria-hidden="true" />{' '}
                                                Remaining to collect{selectedDriver?.driverName ? ` from ${selectedDriver.driverName}` : ''}
                                            </span>
                                            <span><span className="w-custody-tot-label">COD</span> <strong>{formatMoney(remaining.cod, heldCurrency)}</strong></span>
                                            <span><span className="w-custody-tot-label">Duty/Tax</span> <strong>{formatMoney(remaining.dutiesTax, heldCurrency)}</strong></span>
                                            <span className="w-custody-tot-grand"><span className="w-custody-tot-label">Total</span> <strong style={{ color: '#0369a1' }}>{formatMoney(remaining.total, heldCurrency)}</strong></span>
                                            <span style={{ color: '#0369a1', fontSize: 12 }}>{heldItems.length} AWB(s)</span>
                                        </div>
                                    )}
                                </>
                            )}
                            {tab === 'from-supervisor' && (
                                <div className="form-group" style={{ marginBottom: 0, maxWidth: 460 }}>
                                    <label>Supervisor (collector)</label>
                                    <select className="form-control" value={selectedSupervisorId}
                                        onChange={e => setSelectedSupervisorId(e.target.value)} disabled={partiesLoading}>
                                        <option value="">{partiesLoading ? 'Loading supervisors…' : 'Select a supervisor…'}</option>
                                        {supervisors.map(s => (
                                            <option key={s.userId} value={s.userId}>
                                                {(s.personnelName || s.email || `User #${s.userId}`)}{s.email ? ` · ${s.email}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {!partiesLoading && supervisors.length === 0 && (
                                        <small style={{ color: '#b45309' }}>
                                            <i className="fa fa-exclamation-triangle" aria-hidden="true" /> No collectors set up yet — add one in <strong>Wallet Setup</strong>.
                                        </small>
                                    )}
                                </div>
                            )}
                            {tab === 'to-company' && (
                                <div className="form-group" style={{ marginBottom: 0, maxWidth: 460 }}>
                                    <label>Accountant</label>
                                    <select className="form-control" value={selectedAccountantId}
                                        onChange={e => setSelectedAccountantId(e.target.value)} disabled={partiesLoading}>
                                        <option value="">{partiesLoading ? 'Loading accountants…' : 'Select an accountant…'}</option>
                                        {accountants.map(a => (
                                            <option key={a.userId} value={a.userId}>
                                                {(a.personnelName || a.email || `User #${a.userId}`)}{a.email ? ` · ${a.email}` : ''}
                                                {(a.email || '').toLowerCase() === (email || '').toLowerCase() ? ' (you)' : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {!partiesLoading && accountants.length === 0 && (
                                        <small style={{ color: '#b45309' }}>
                                            <i className="fa fa-exclamation-triangle" aria-hidden="true" /> No accountants set up yet — add one in <strong>Wallet Setup</strong>.
                                        </small>
                                    )}
                                    <small style={{ display: 'block', marginTop: 6, color: '#6b7280' }}>
                                        AWBs this accountant is holding. You can only submit AWBs you collected yourself.
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Held-AWB checklist + live sum */}
                    <div className="w-card">
                        <div className="w-card-header">
                            <span className="w-card-title"><i className="fa fa-list-ul" aria-hidden="true" /> Held AWBs</span>
                            <span className="w-card-meta">{formatInt(heldItems.length)} AWB(s)</span>
                        </div>
                        <div className="w-card-body">
                            {!partyChosen ? (
                                <EmptyState
                                    icon="fa-hand-pointer-o"
                                    title={tab === 'to-company' ? 'Select an accountant to see their held AWBs' : `Pick a ${tab === 'from-driver' ? 'driver' : 'supervisor'} to see their held AWBs`}
                                />
                            ) : (
                                <AwbChecklist
                                    items={heldItems}
                                    selected={selected}
                                    onToggle={toggle}
                                    onToggleAll={toggleAll}
                                    loading={heldLoading}
                                    emptyTitle="No AWBs held here right now"
                                    emptySubtitle="Nothing to hand over at this stage."
                                />
                            )}
                        </div>
                    </div>

                    {/* Sticky action bar with the live selected COD / Duty-Tax / Total */}
                    {partyChosen && heldItems.length > 0 && (
                        <div className="w-custody-actionbar">
                            <div className="w-custody-sum">
                                <span className="w-custody-sum-count">
                                    <i className="fa fa-check-square-o" aria-hidden="true" /> {formatInt(selected.size)} / {formatInt(heldItems.length)} AWB(s)
                                </span>
                                <span className="w-custody-tot">
                                    <span className="w-custody-tot-label">COD</span>
                                    <span className="w-custody-tot-val">{formatMoney(totals.cod, heldCurrency)}</span>
                                </span>
                                <span className="w-custody-tot">
                                    <span className="w-custody-tot-label">Duty/Tax</span>
                                    <span className="w-custody-tot-val">{formatMoney(totals.dutiesTax, heldCurrency)}</span>
                                </span>
                                <span className="w-custody-tot w-custody-tot-grand">
                                    <span className="w-custody-tot-label">Grand total</span>
                                    <span className="w-custody-tot-val">{formatMoney(totals.total, heldCurrency)}</span>
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="w-btn w-btn-ghost" onClick={clearSelection} disabled={!selected.size}>
                                    <i className="fa fa-eraser" aria-hidden="true" /> Clear
                                </button>
                                <button className="w-btn w-btn-primary" onClick={openConfirm} disabled={!selected.size}>
                                    <i className="fa fa-check" aria-hidden="true" /> {confirmCta}
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* ── COD Trace (ledger): every AWB, amount, current stage & who holds the cash ── */}
            {tab === 'ledger' && (
                <>
                    <div className="w-toolbar">
                        <span className="w-toolbar-label"><i className="fa fa-list-ul" aria-hidden="true" /> COD Trace</span>
                        <div className="w-input-wrap">
                            <i className="fa fa-search" aria-hidden="true" />
                            <input placeholder="Search AWB, driver or holder…" value={ledgerSearch}
                                onChange={e => setLedgerSearch(e.target.value)} />
                        </div>
                        <select className="form-control" style={{ maxWidth: 190 }} value={ledgerStage} onChange={e => setLedgerStage(e.target.value)}>
                            <option value="ALL">All stages</option>
                            <option value="DRIVER">With driver</option>
                            <option value="SUPERVISOR">With supervisor</option>
                            <option value="ACCOUNT">With accountant</option>
                            <option value="COMPANY">Deposited (company)</option>
                        </select>
                        <button className="w-btn w-btn-ghost w-btn-sm" onClick={loadLedger} disabled={ledgerLoading}>
                            <i className={`fa fa-refresh ${ledgerLoading ? 'fa-spin' : ''}`} aria-hidden="true" /> Refresh
                        </button>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, margin: '4px 0 14px', alignItems: 'center' }}>
                        <span className="w-id-chip">{formatInt(ledgerFiltered.length)} AWB(s)</span>
                        <span><span className="w-custody-tot-label">COD</span> <strong>{formatMoney(ledgerTotals.cod, ledgerCurrency)}</strong></span>
                        <span><span className="w-custody-tot-label">Duty/Tax</span> <strong>{formatMoney(ledgerTotals.dt, ledgerCurrency)}</strong></span>
                        <span className="w-custody-tot-grand"><span className="w-custody-tot-label">Total</span> <strong>{formatMoney(ledgerTotals.total, ledgerCurrency)}</strong></span>
                    </div>

                    <div className="w-card">
                        <div className="w-card-body">
                            {ledgerLoading ? (
                                <WalletSpinner label="Loading COD trace…" />
                            ) : ledgerFiltered.length === 0 ? (
                                <EmptyState icon="fa-list-ul" title="No COD AWBs in custody yet"
                                    subtitle="AWBs show here once a driver's COD is collected up the chain." />
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="w-rtable-table" style={{ width: '100%' }}>
                                        <thead>
                                            <tr>
                                                <th>AWB</th><th>Driver</th>
                                                <th style={{ textAlign: 'right' }}>COD</th>
                                                <th style={{ textAlign: 'right' }}>Duty/Tax</th>
                                                <th style={{ textAlign: 'right' }}>Total</th>
                                                <th>Held by</th><th>Updated</th><th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ledgerFiltered.map(r => {
                                                const m = stageMeta(r.stage);
                                                const updated = r.companyAt || r.accountAt || r.supervisorAt || r.collectedAt;
                                                return (
                                                    <tr key={r.awb} style={{ cursor: 'pointer' }} onClick={() => openTrail(r.awb)} title="View full trail">
                                                        <td><span className="w-id-chip">{r.awb}</span></td>
                                                        <td>{r.driverName || (r.driverId ? `Driver #${r.driverId}` : '—')}</td>
                                                        <td style={{ textAlign: 'right' }}>{formatMoney(r.codAmount, r.currency)}</td>
                                                        <td style={{ textAlign: 'right' }}>{formatMoney(r.dutiesTaxAmount, r.currency)}</td>
                                                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatMoney(r.amount, r.currency)}</td>
                                                        <td style={{ whiteSpace: 'nowrap' }}>
                                                            <Pill tone={m.tone} icon={m.icon}>{m.label}</Pill>
                                                            <span style={{ marginLeft: 8 }}>{r.holderName || '—'}</span>
                                                        </td>
                                                        <td style={{ whiteSpace: 'nowrap', color: '#6b7280', fontSize: 12 }}>{updated ? formatDateTime(updated) : '—'}</td>
                                                        <td style={{ textAlign: 'right', color: 'var(--w-primary)' }}><i className="fa fa-history" aria-hidden="true" /></td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* ── AWB trail lookup ── */}
            {tab === 'trail' && (
                <>
                    <div className="w-toolbar">
                        <span className="w-toolbar-label"><i className="fa fa-search" aria-hidden="true" /> AWB trail</span>
                        <div className="w-input-wrap">
                            <i className="fa fa-barcode" aria-hidden="true" />
                            <input
                                placeholder="Enter an AWB (e.g. UB04-26A6071_1)…"
                                value={trailInput}
                                onChange={e => setTrailInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') runTrail(); }}
                            />
                        </div>
                        <button className="w-btn w-btn-primary w-btn-sm" onClick={runTrail} disabled={trailLoading || !trailInput.trim()}>
                            {trailLoading ? <><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Looking up…</> : <><i className="fa fa-search" aria-hidden="true" /> Look up</>}
                        </button>
                        {(trailInput || trail || trailSearched) && (
                            <button className="w-btn w-btn-ghost w-btn-sm" onClick={() => { setTrailInput(''); setTrail(null); setTrailSearched(false); }}>
                                <i className="fa fa-times" aria-hidden="true" /> Clear
                            </button>
                        )}
                    </div>

                    {trailLoading ? (
                        <div className="w-card"><div className="w-card-body"><WalletSpinner label="Loading trail…" /></div></div>
                    ) : trailSearched && !trail ? (
                        <div className="w-card"><div className="w-card-body">
                            <EmptyState icon="fa-search" title="No trail found for that AWB" subtitle="Double-check the AWB and try again." />
                        </div></div>
                    ) : trail ? (
                        <TrailView trail={trail} />
                    ) : (
                        <div className="w-card"><div className="w-card-body">
                            <EmptyState icon="fa-list-alt" title="Look up an AWB to see its chain of custody" subtitle="Driver → Supervisor → Account → Company." />
                        </div></div>
                    )}
                </>
            )}

            {/* Confirm (signature) modal */}
            {confirmOpen && (
                <div className="modal-overlay w-modal">
                    <div className="modal-content" style={{ maxWidth: 520 }}>
                        <div className="w-modal-header">
                            <div className="w-modal-icon"><i className="fa fa-exchange" aria-hidden="true" /></div>
                            <div style={{ flex: 1 }}>
                                <h3>{confirmCta}</h3>
                                <p>Signature required — the {signerLabel} sign(s) to confirm the hand-off.</p>
                            </div>
                            <button className="close-btn" onClick={() => setConfirmOpen(false)}><i className="fa fa-times" aria-hidden="true" /></button>
                        </div>
                        <div className="modal-body">
                            <div className="w-summary-line" style={{ display: 'block' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                                    <span>{formatInt(selected.size)} AWB(s) selected</span>
                                </div>
                                <div className="w-custody-breakdown">
                                    <span><span className="w-custody-tot-label">COD</span> {formatMoney(totals.cod, heldCurrency)}</span>
                                    <span><span className="w-custody-tot-label">Duty/Tax</span> {formatMoney(totals.dutiesTax, heldCurrency)}</span>
                                    <span className="w-custody-tot-grand"><span className="w-custody-tot-label">Grand total</span> <strong>{formatMoney(totals.total, heldCurrency)}</strong></span>
                                </div>
                            </div>
                            <div className="w-custody-awb-pills">
                                {Array.from(selected).slice(0, 40).map(awb => (
                                    <span key={awb} className="w-id-chip">{awb}</span>
                                ))}
                                {selected.size > 40 && <span className="w-id-chip">+{selected.size - 40} more</span>}
                            </div>
                            <div className="form-group"><label>Notes</label>
                                <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional (e.g. reference / shift)" />
                            </div>
                            <div className="form-group">
                                <label>Signature ({signerLabel} — handing over)</label>
                                <SignaturePad onChange={setSignature} hint={`Sign above to confirm the hand-off of ${formatInt(selected.size)} AWB(s).`} />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="w-btn w-btn-ghost" onClick={() => setConfirmOpen(false)} disabled={saving}>Cancel</button>
                            <button className="w-btn w-btn-primary" onClick={submitHandoff} disabled={saving || !signature}>
                                {saving ? <><i className="fa fa-spinner fa-spin" aria-hidden="true" /> Submitting…</> : <><i className="fa fa-check" aria-hidden="true" /> Confirm</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Trail result: current stage + timeline ──────────────────────────────────
function TrailView({ trail }) {
    const current = trail.current || {};
    const cur = stageMeta(current.stage);
    return (
        <>
            <div className="w-driver-card">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span className="w-id-chip" style={{ fontSize: 13 }}>{trail.awb}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <Pill tone={cur.tone} icon={cur.icon}>Now at: {cur.label}</Pill>
                        {current.driverName || current.driverId ? (
                            <Person name={current.driverName || `Driver #${current.driverId}`} sub={`Driver #${current.driverId}`} size={30} />
                        ) : null}
                    </div>
                </div>
                <div className="w-driver-balance">
                    <div className="w-stat-label">Total amount</div>
                    <div className="w-stat-value">{formatMoney(current.amount, current.currency)}</div>
                    <div className="w-stat-sub" style={{ color: '#6b7280' }}>
                        COD {formatMoney(current.codAmount, current.currency)} · Duty/Tax {formatMoney(current.dutiesTaxAmount, current.currency)}
                    </div>
                </div>
            </div>

            {/* Stage progress strip */}
            <div className="w-card">
                <div className="w-card-body">
                    <div className="w-custody-stages-strip">
                        {CUSTODY_STAGES.map((st, i) => {
                            const m = stageMeta(st);
                            const reachedIdx = CUSTODY_STAGES.indexOf(current.stage);
                            const done = reachedIdx >= 0 && i <= reachedIdx;
                            const ts = stageTimestamp(current, st);
                            return (
                                <React.Fragment key={st}>
                                    <div className={`w-css-step ${done ? 'done' : ''}`}>
                                        <span className="w-css-dot"><i className={`fa ${done ? m.icon : 'fa-circle-o'}`} aria-hidden="true" /></span>
                                        <span className="w-css-label">{m.label}</span>
                                        {ts ? <span className="w-css-time">{formatDateTime(ts)}</span> : null}
                                    </div>
                                    {i < CUSTODY_STAGES.length - 1 ? <span className={`w-css-bar ${done && i < reachedIdx ? 'done' : ''}`} /> : null}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="w-card">
                <div className="w-card-header">
                    <span className="w-card-title"><i className="fa fa-history" aria-hidden="true" /> Hand-off timeline</span>
                    <span className="w-card-meta">{formatInt((trail.steps || []).length)} step(s)</span>
                </div>
                <div className="w-card-body">
                    <CustodyTimeline steps={trail.steps} />
                </div>
            </div>
        </>
    );
}
